import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { artworks, artistInfo, collections } from '../data/artworksData';

const HomePage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';
  const [currentSlide, setCurrentSlide] = useState(0);

  // Get featured artworks for hero
  const featuredArtworks = artworks.filter(a => a.featured).slice(0, 5);

  // Auto-advance slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredArtworks.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [featuredArtworks.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredArtworks.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredArtworks.length) % featuredArtworks.length);
  };

  return (
    <div className="min-h-screen bg-gallery-bg">
      {/* Hero Section - Full Screen Featured Artwork */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Artwork */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${featuredArtworks[currentSlide]?.image})`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
          </motion.div>
        </AnimatePresence>

        {/* Hero Content - Mobile-first responsive */}
        <div className="relative z-10 text-center text-white px-4 sm:px-6 max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-xs sm:text-sm md:text-base tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-4 sm:mb-6 text-white/80"
          >
            {lang === 'ru' ? 'Персональная выставка' : 'Personal Exhibition'}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="font-serif text-fluid-hero font-light mb-4 sm:mb-6 tracking-wide"
          >
            {artistInfo.name[lang]}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8 sm:mb-10 font-light leading-relaxed px-2"
          >
            {artistInfo.statement[lang].substring(0, 150)}...
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0"
          >
            <Link
              to="/gallery"
              className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-white text-gallery-text px-6 sm:px-8 py-4 min-h-[48px] text-sm tracking-wider uppercase hover:bg-gallery-accent hover:text-white active:scale-[0.98] transition-all duration-300"
            >
              {lang === 'ru' ? 'Смотреть работы' : 'View Works'}
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 sm:gap-3 border border-white/50 text-white px-6 sm:px-8 py-4 min-h-[48px] text-sm tracking-wider uppercase hover:bg-white hover:text-gallery-text active:scale-[0.98] transition-all duration-300"
            >
              {lang === 'ru' ? 'О художнике' : 'About Artist'}
            </Link>
          </motion.div>
        </div>

        {/* Slider Navigation - Responsive */}
        <div className="absolute bottom-4 sm:bottom-8 left-0 right-0 z-20 safe-area-inset-bottom">
          <div className="container mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Slide Info - Hidden on very small screens */}
            <motion.div
              key={`info-${currentSlide}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-white hidden xs:block text-center sm:text-left"
            >
              <p className="text-xs sm:text-sm text-white/60 mb-1">
                {featuredArtworks[currentSlide]?.year}
              </p>
              <h3 className="font-serif text-base sm:text-xl">
                {featuredArtworks[currentSlide]?.title[lang]}
              </h3>
            </motion.div>

            {/* Navigation Controls - Touch-friendly */}
            <div className="flex items-center gap-4 sm:gap-6">
              <button
                onClick={prevSlide}
                className="flex items-center justify-center w-11 h-11 text-white/70 hover:text-white active:text-white transition-colors"
                aria-label="Previous slide"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Slide Indicators - Touch-friendly */}
              <div className="flex gap-2 sm:gap-2">
                {featuredArtworks.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`w-8 sm:w-12 h-6 flex items-center justify-center transition-all duration-300`}
                    aria-label={`Go to slide ${idx + 1}`}
                    aria-current={idx === currentSlide ? 'true' : 'false'}
                  >
                    <span className={`block w-full h-0.5 ${
                      idx === currentSlide ? 'bg-white' : 'bg-white/30'
                    }`} />
                  </button>
                ))}
              </div>

              <button
                onClick={nextSlide}
                className="flex items-center justify-center w-11 h-11 text-white/70 hover:text-white active:text-white transition-colors"
                aria-label="Next slide"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden md:block"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
          >
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* Statement Section - Mobile-first */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-12 sm:w-16 h-px bg-gallery-accent mx-auto mb-6 sm:mb-8" />
            <blockquote className="font-serif text-xl sm:text-2xl md:text-3xl text-gallery-text font-light leading-relaxed italic px-2">
              "{artistInfo.statement[lang]}"
            </blockquote>
            <p className="mt-6 sm:mt-8 text-gallery-muted tracking-wider uppercase text-xs sm:text-sm">
              {artistInfo.name[lang]}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Works Section - Mobile-first */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 bg-gallery-bg">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 sm:mb-12 md:mb-16"
          >
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-gallery-text mb-3 sm:mb-4">
              {lang === 'ru' ? 'Избранные работы' : 'Featured Works'}
            </h2>
            <p className="text-gallery-muted max-w-xl mx-auto text-sm sm:text-base px-2">
              {lang === 'ru'
                ? 'Наиболее значимые произведения в творчестве художника'
                : 'The most significant works in the artist\'s creative journey'
              }
            </p>
          </motion.div>

          {/* Works Grid - Responsive: 1 col mobile, 2 col tablet, 3 col desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10 lg:gap-12">
            {featuredArtworks.slice(0, 6).map((artwork, idx) => (
              <motion.div
                key={artwork.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              >
                <Link
                  to={`/artwork/${artwork.id}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden bg-gallery-light mb-4 sm:mb-6">
                    <div className="aspect-[4/5]">
                      <img
                        src={artwork.image}
                        alt={artwork.title[lang]}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    {/* Status Badge - Responsive positioning */}
                    {artwork.status !== 'available' && (
                      <div className="absolute top-2 right-2 sm:top-4 sm:right-4">
                        <span className={`px-2 sm:px-3 py-1 text-xs tracking-wider uppercase ${
                          artwork.status === 'sold'
                            ? 'bg-gallery-text text-white'
                            : 'bg-gallery-accent text-white'
                        }`}>
                          {artwork.status === 'sold'
                            ? (lang === 'ru' ? 'Продано' : 'Sold')
                            : (lang === 'ru' ? 'Резерв' : 'Reserved')
                          }
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="text-center px-2">
                    <h3 className="font-serif text-lg sm:text-xl text-gallery-text mb-1 sm:mb-2 group-hover:text-gallery-accent transition-colors">
                      {artwork.title[lang]}
                    </h3>
                    <p className="text-gallery-muted text-xs sm:text-sm">
                      {artwork.technique[lang]}, {artwork.year}
                    </p>
                    <p className="text-gallery-muted text-xs sm:text-sm mt-0.5 sm:mt-1">
                      {artwork.size}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* View All Link - Touch-friendly */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-10 sm:mt-12 md:mt-16"
          >
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 sm:gap-3 text-gallery-text hover:text-gallery-accent active:text-gallery-accent transition-colors text-sm tracking-wider uppercase min-h-[44px] px-4 py-2"
            >
              {lang === 'ru' ? 'Смотреть все работы' : 'View All Works'}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Collections Section - Mobile-first */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-12 md:mb-16"
          >
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-gallery-text mb-3 sm:mb-4">
              {lang === 'ru' ? 'Серии и коллекции' : 'Series & Collections'}
            </h2>
            <p className="text-gallery-muted max-w-xl mx-auto text-sm sm:text-base px-2">
              {lang === 'ru'
                ? 'Тематические циклы работ'
                : 'Thematic cycles of works'
              }
            </p>
          </motion.div>

          {/* Collections Grid - Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {collections.slice(0, 6).map((collection, idx) => {
              const collectionArtworks = artworks.filter(a => a.series === collection.id);
              const previewImage = collectionArtworks[0]?.image || '/images/1.jpeg';

              return (
                <motion.div
                  key={collection.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link
                    to={`/gallery?series=${collection.id}`}
                    className="group block relative overflow-hidden"
                  >
                    <div className="aspect-[16/9]">
                      <img
                        src={previewImage}
                        alt={collection.title[lang]}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 active:bg-black/50 transition-colors" />
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 sm:p-6">
                      <h3 className="font-serif text-xl sm:text-2xl mb-1 sm:mb-2 text-center">
                        {collection.title[lang]}
                      </h3>
                      <p className="text-white/80 text-xs sm:text-sm text-center max-w-xs line-clamp-2">
                        {collection.description[lang]}
                      </p>
                      <span className="mt-3 sm:mt-4 text-xs tracking-wider uppercase opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                        {collectionArtworks.length} {lang === 'ru' ? 'работ' : 'works'}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visit Section - Mobile-first with safe area */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 bg-gallery-text text-white safe-area-inset-bottom">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl mb-4 sm:mb-6">
                {lang === 'ru' ? 'Посетите выставку' : 'Visit the Exhibition'}
              </h2>
              <p className="text-white/70 mb-6 sm:mb-8 max-w-xl mx-auto text-sm sm:text-base px-2">
                {lang === 'ru'
                  ? 'Приглашаем вас на просмотр работ в нашей галерее. Запись по предварительной договоренности.'
                  : 'We invite you to view the works in our gallery. By appointment only.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-white text-gallery-text px-6 sm:px-8 py-4 min-h-[48px] text-sm tracking-wider uppercase hover:bg-gallery-accent hover:text-white active:scale-[0.98] transition-all duration-300"
                >
                  {lang === 'ru' ? 'Записаться на просмотр' : 'Schedule a Visit'}
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 sm:gap-3 border border-white/30 text-white px-6 sm:px-8 py-4 min-h-[48px] text-sm tracking-wider uppercase hover:bg-white hover:text-gallery-text active:scale-[0.98] transition-all duration-300"
                >
                  {lang === 'ru' ? 'Связаться с нами' : 'Contact Us'}
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
